// Case Data grid: the two-party input rows in the documented order (plan §3.2), with our own labels. Composite rows
// expand inline to their detail fields; every accessor reads/writes the shared case schema (money is MONTHLY).
import type { Case, FilingStatus, PartyIndex, PartyInputs } from '@divorceos/schema';
import { incomeTotal } from '../lib/rows';

export type SubKind = 'money' | 'count' | 'percent' | 'bool' | 'text' | 'date';
export type RowKind = 'info' | 'nkids' | 'ncp' | 'filing' | 'exempt' | 'money' | 'composite';

export interface SubRow {
  id: string;
  label: string;
  kind: SubKind;
  get: (c: Case, p: PartyIndex) => number | string | boolean;
  set: (c: Case, p: PartyIndex, v: number | string | boolean) => void;
  /** Read-only while this holds (e.g. automatic hardship amount). */
  lockedWhen?: (c: Case, p: PartyIndex) => boolean;
  /** Shown on the bench report's input block (text/bool/date lines are not). */
  onReport?: boolean;
}

export interface GridRow {
  id: string;
  label: string;
  kind: RowKind;
  subs?: SubRow[];
  /** Leaf money rows. */
  get?: (p: PartyInputs) => number;
  set?: (p: PartyInputs, v: number) => void;
  /** Composite rows: the parent total (monthly money unless `countTotal`). */
  total?: (c: Case, p: PartyIndex) => number;
  countTotal?: boolean;
  /** Automatic value marker (asterisk). */
  auto?: (c: Case, p: PartyIndex) => boolean;
}

type Pick = (p: PartyInputs) => Record<string, number>;
const num = (v: number | string | boolean): number => (typeof v === 'number' ? v : Number(v) || 0);
const P = (c: Case, p: PartyIndex): PartyInputs => c.parties[p];

/** Money detail line on a nested object of PartyInputs. */
function m(id: string, label: string, pick: Pick, key: string): SubRow {
  return { id, label, kind: 'money', onReport: true, get: (c, p) => pick(P(c, p))[key] ?? 0, set: (c, p, v) => { pick(P(c, p))[key] = num(v); } };
}
/** Money detail line on a top-level field of PartyInputs. */
function top(id: string, label: string, key: 'otherMedical' | 'propertyTax' | 'contributions'): SubRow {
  return { id, label, kind: 'money', onReport: true, get: (c, p) => P(c, p)[key], set: (c, p, v) => { P(c, p)[key] = num(v); } };
}
const sumSubs = (subs: SubRow[]) => (c: Case, p: PartyIndex): number =>
  subs.filter((s) => s.kind === 'money').reduce((a, s) => a + num(s.get(c, p)), 0);

const ot = (p: PartyInputs) => p.otherTaxable as unknown as Record<string, number>;
const ns = (p: PartyInputs) => p.newSpouseIncome as unknown as Record<string, number>;
const nt = (p: PartyInputs) => p.nonTaxable as unknown as Record<string, number>;
const adj = (p: PartyInputs) => p.adjustments as unknown as Record<string, number>;
const ssp = (p: PartyInputs) => p.ssPaidPrior as unknown as Record<string, number>;
const hi = (p: PartyInputs) => p.healthInsurance as unknown as Record<string, number>;
const ie = (p: PartyInputs) => p.interestExpense as unknown as Record<string, number>;
const md = (p: PartyInputs) => p.miscDeductions as unknown as Record<string, number>;
const mr = (p: PartyInputs) => p.mandatoryRetirement as unknown as Record<string, number>;
const hs = (p: PartyInputs) => p.hardship as unknown as Record<string, number>;
const og = (p: PartyInputs) => p.otherGuidelineDeductions as unknown as Record<string, number>;
const amt = (p: PartyInputs) => p.amt as unknown as Record<string, number>;
const cc = (p: PartyInputs) => p.childCare as unknown as Record<string, number>;
const tanf = (p: PartyInputs) => p as unknown as Record<string, number>;

const OTHER_TAXABLE: SubRow[] = [
  m('oInt', 'Interest', ot, 'interest'),
  m('oDivQ', 'Qualified dividends', ot, 'qualifiedDividends'),
  m('oDivN', 'Ordinary (nonqualified) dividends', ot, 'nonQualifiedDividends'),
  m('oCgS', 'Short-term capital gain', ot, 'capGainShort'),
  m('oCgL', 'Long-term capital gain', ot, 'capGainLong'),
  m('oCg28', '28% rate gain', ot, 'rateGain28'),
  m('oCg1250', 'Unrecaptured section 1250 gain', ot, 'unrecaptured1250'),
  m('oRent', 'Rental income (net)', ot, 'rental'),
  m('oRoy', 'Royalties', ot, 'royalty'),
  m('oInv', 'Other investment income', ot, 'otherInvestment'),
  m('oSsa', 'Social Security benefits', ot, 'socialSecurity'),
  m('oUnemp', 'Unemployment compensation', ot, 'unemployment'),
  m('oAlimT', 'Spousal support received (federally taxable)', ot, 'alimonyReceivedFedTaxable'),
  m('oAlimN', 'Spousal support received (federally nontaxable)', ot, 'alimonyReceivedFedNonTaxable'),
  m('oAlimP', 'Partner support received', ot, 'partnerAlimonyReceived'),
  m('oMisc', 'Pension, annuity & other taxable income', ot, 'other'),
];

const NONTAX: SubRow[] = [
  m('ntCsOnly', 'Income counted for child support only', nt, 'forChildSupportOnly'),
  m('ntExInt', 'Tax-exempt interest', nt, 'taxExemptInterest'),
  m('ntOther', 'Other nontaxable income', nt, 'other'),
];

const NEW_SPOUSE: SubRow[] = [
  m('nsWages', 'New spouse wages', ns, 'wages'),
  m('nsSe', 'New spouse self-employment income', ns, 'selfEmployment'),
  m('nsInt', 'New spouse interest', ns, 'interest'),
  m('nsDiv', 'New spouse dividends', ns, 'nonQualifiedDividends'),
  m('nsOther', 'New spouse other taxable income', ns, 'other'),
];

const ATI: SubRow[] = [
  m('atiSep', 'SEP and SIMPLE contributions', adj, 'sepSimple'),
  m('atiOther', 'Other adjustments', adj, 'otherAti'),
  m('atiChar', 'Charitable (above the line)', adj, 'charitable'),
];

const SS_PAID: SubRow[] = [
  m('sspDed', 'Paid by this party, federally deductible', ssp, 'thisSpouseFedDeductible'),
  m('sspNon', 'Paid by this party, not federally deductible', ssp, 'thisSpouseFedNonDeductible'),
  m('sspNsDed', 'Paid by new spouse, federally deductible', ssp, 'newSpouseFedDeductible'),
  m('sspNsNon', 'Paid by new spouse, not federally deductible', ssp, 'newSpouseFedNonDeductible'),
  m('sspPart', 'Partner support paid by this party', ssp, 'partnerByThisSpouse'),
  m('sspPartNs', 'Partner support paid by new spouse', ssp, 'partnerByNewSpouse'),
];

const HEALTH: SubRow[] = [
  m('hiPaid', 'Premiums paid by the party', hi, 'paidByParty'),
  m('hiPre', 'Pre-tax payroll deduction', hi, 'wageDeductionPreTax'),
];

const ITEMIZED: SubRow[] = [
  top('medical', 'Medical, beyond insurance', 'otherMedical'),
  top('proptax', 'Property tax', 'propertyTax'),
  m('interest', 'Home mortgage interest', ie, 'homeMortgage'),
  m('invInt', 'Investment interest', ie, 'investmentInterest'),
  top('contrib', 'Charitable contributions', 'contributions'),
  m('salesTax', 'State sales tax', md, 'stateSalesTax'),
  m('invExp', 'Investment expenses', md, 'investmentExpenses'),
  m('miscitem', 'Other itemized', md, 'other'),
];

const RETIRE: SubRow[] = [
  m('mrAti', 'Qualified (pre-tax) contributions', mr, 'qualifiedAti'),
  m('mrOther', 'Other required contributions', mr, 'other'),
];

const HARDSHIP: SubRow[] = [
  { id: 'hsKids', label: 'Other children in the home', kind: 'count', get: (c, p) => P(c, p).hardship.otherSupportedChildren, set: (c, p, v) => { P(c, p).hardship.otherSupportedChildren = Math.max(0, Math.round(num(v))); } },
  { id: 'hsCalc', label: 'Compute the children amount automatically', kind: 'bool', get: (c, p) => P(c, p).hardship.calculate, set: (c, p, v) => { P(c, p).hardship.calculate = Boolean(v); } },
  { ...m('hsAmt', 'Children amount (FC 4071(b))', hs, 'totalFc4071b'), lockedWhen: (c, p) => P(c, p).hardship.calculate },
  { id: 'hsMult', label: 'Share of the children amount allowed', kind: 'percent', get: (c, p) => P(c, p).hardship.multiplyPercent, set: (c, p, v) => { P(c, p).hardship.multiplyPercent = num(v); } },
  m('hsHealth', 'Extraordinary health expenses', hs, 'extraordinaryHealth'),
  m('hsLoss', 'Uninsured catastrophic losses', hs, 'catastrophicLosses'),
];

const OTHER_GDL: SubRow[] = [
  m('ogJob', 'Necessary job-related expenses', og, 'jobRelated'),
  m('ogOther', 'Other allowed deductions', og, 'other'),
];

const AMT: SubRow[] = [
  m('amtMort', 'Home mortgage interest adjustment', amt, 'homeMortgageInterest'),
  m('amtInv', 'Investment interest adjustment', amt, 'investmentInterest'),
  m('amtDep', 'Depletion', amt, 'depletion'),
  m('amtNol', 'Net operating loss deduction', amt, 'netOperatingLoss'),
  m('amtAdj', 'Other AMT adjustments', amt, 'otherAdjustments'),
];

const ADDONS: SubRow[] = [
  m('childcare', 'Child care, work or training · mandatory', cc, 'thisRelationship'),
  m('unins', 'Uninsured health costs · mandatory', cc, 'uninsuredHealth'),
  m('travel', 'Education, special needs & visitation travel · discretionary', cc, 'visitsSchoolTravel'),
  m('ccOther', 'Child care, other children in the home', cc, 'otherChildrenInHome'),
];

const TANF: SubRow[] = [
  m('tanfAid', 'TANF and SSI received', tanf, 'tanfReceived'),
  m('tanfCs', 'Child support received, other relationships', tanf, 'childSupportReceived'),
];

const EXEMPT: SubRow[] = [
  { id: 'exAuto', label: 'Count automatically from the children', kind: 'bool', get: (c, p) => P(c, p).exemptions.auto, set: (c, p, v) => { P(c, p).exemptions.auto = Boolean(v); } },
  ...(['personal', 'dependent', 'qualifyingChildrenCtc', 'qualifyingChildrenEic', 'qualifyingChildrenCcc', 'childrenUnder6', 'otherDependents'] as const).map((k): SubRow => ({
    id: `ex_${k}`,
    label: { personal: 'Personal', dependent: 'Dependents', qualifyingChildrenCtc: 'Children for the child tax credit', qualifyingChildrenEic: 'Children for the earned income credit', qualifyingChildrenCcc: 'Children for the child care credit', childrenUnder6: 'Children under 6', otherDependents: 'Other dependents' }[k],
    kind: 'count',
    get: (c, p) => P(c, p).exemptions[k],
    set: (c, p, v) => { P(c, p).exemptions[k] = Math.max(0, Math.round(num(v))); },
    lockedWhen: (c, p) => P(c, p).exemptions.auto,
  })),
];

const PARTY_INFO: SubRow[] = [
  { id: 'pname', label: 'Name', kind: 'text', get: (c, p) => c.info.partyNames[p] ?? '', set: (c, p, v) => { c.info.partyNames[p] = String(v); } },
  { id: 'dob', label: 'Date of birth', kind: 'date', get: (c, p) => P(c, p).taxInfo.dateOfBirth ?? '', set: (c, p, v) => { P(c, p).taxInfo.dateOfBirth = String(v) || undefined; } },
];

/** The grid, in the documented order (TANF at the bottom). */
export const ROWS: GridRow[] = [
  { id: 'party', label: 'Party information', kind: 'info', subs: PARTY_INFO },
  { id: 'nkids', label: 'Number of children', kind: 'nkids' },
  { id: 'ncp', label: 'Timeshare with NCP', kind: 'ncp' },
  { id: 'filing', label: 'Filing status', kind: 'filing' },
  { id: 'exempt', label: 'Dependents claimed (federal)', kind: 'exempt', subs: EXEMPT, countTotal: true, auto: (c, p) => P(c, p).exemptions.auto },
  { id: 'wages', label: 'Wages + salary', kind: 'money', get: (p) => p.wages, set: (p, v) => { p.wages = v; } },
  { id: 'k401', label: '401(k) employee contribution', kind: 'money', get: (p) => p.employee401k, set: (p, v) => { p.employee401k = v; } },
  { id: 'se', label: 'Self-employment income', kind: 'money', get: (p) => p.selfEmployment, set: (p, v) => { p.selfEmployment = v; } },
  { id: 'other', label: 'Other taxable income', kind: 'composite', subs: OTHER_TAXABLE, total: (c, p) => incomeTotal(P(c, p).otherTaxable) },
  { id: 'nontax', label: 'Other nontaxable income', kind: 'composite', subs: NONTAX, total: sumSubs(NONTAX) },
  { id: 'spouse', label: 'New-spouse income', kind: 'composite', subs: NEW_SPOUSE, total: (c, p) => { const n = P(c, p).newSpouseIncome; return n.wages + n.selfEmployment + incomeTotal(n); } },
  { id: 'ati', label: 'Adjustments to income', kind: 'composite', subs: ATI, total: sumSubs(ATI) },
  { id: 'sspaid', label: 'Spousal support paid, other marriage', kind: 'composite', subs: SS_PAID, total: sumSubs(SS_PAID) },
  { id: 'cspaid', label: 'Child support paid, other relationship', kind: 'money', get: (p) => p.csPaidPrior, set: (p, v) => { p.csPaidPrior = v; } },
  { id: 'qbid', label: 'Qualified business income deduction', kind: 'money', get: (p) => p.qbid, set: (p, v) => { p.qbid = v; if (p.qbidWorksheet) p.qbidWorksheet.use = false; }, auto: (c, p) => P(c, p).qbidWorksheet?.use === true },
  { id: 'health', label: 'Health insurance', kind: 'composite', subs: HEALTH, total: sumSubs(HEALTH) },
  { id: 'itemized', label: 'Itemized deductions', kind: 'composite', subs: ITEMIZED, total: sumSubs(ITEMIZED) },
  { id: 'union', label: 'Required union dues', kind: 'money', get: (p) => p.unionDues, set: (p, v) => { p.unionDues = v; } },
  { id: 'mandret', label: 'Mandatory retirement', kind: 'composite', subs: RETIRE, total: sumSubs(RETIRE) },
  { id: 'hardship', label: 'Hardship deduction', kind: 'composite', subs: HARDSHIP, total: (c, p) => { const h = P(c, p).hardship; return h.totalFc4071b + h.extraordinaryHealth + h.catastrophicLosses; }, auto: (c, p) => P(c, p).hardship.calculate && P(c, p).hardship.otherSupportedChildren > 0 },
  { id: 'othergdl', label: 'Other guideline deductions', kind: 'composite', subs: OTHER_GDL, total: sumSubs(OTHER_GDL) },
  { id: 'amt', label: 'AMT information (Form 6251)', kind: 'composite', subs: AMT, total: sumSubs(AMT) },
  { id: 'addons', label: 'Child support add-ons', kind: 'composite', subs: ADDONS, total: sumSubs(ADDONS) },
  { id: 'tanf', label: 'TANF, SSI and child support received', kind: 'composite', subs: TANF, total: sumSubs(TANF) },
];

/** Documented row order (plan §3.2). Kept separately so a test pins it. */
export const ROW_ORDER = ['party', 'nkids', 'ncp', 'filing', 'exempt', 'wages', 'k401', 'se', 'other', 'nontax', 'spouse', 'ati', 'sspaid', 'cspaid', 'qbid', 'health', 'itemized', 'union', 'mandret', 'hardship', 'othergdl', 'amt', 'addons', 'tanf'];

export const ROW_BY_ID: Record<string, GridRow> = Object.fromEntries(ROWS.map((r) => [r.id, r]));
export const SUB_PARENT: Record<string, string> = Object.fromEntries(ROWS.flatMap((r) => (r.subs ?? []).map((s) => [s.id, r.id])));
export const SUB_BY_ID: Record<string, SubRow> = Object.fromEntries(ROWS.flatMap((r) => (r.subs ?? []).map((s) => [s.id, s])));

/** Label of a row or detail line id. */
export function rowLabel(id: string): string {
  return ROW_BY_ID[id]?.label ?? SUB_BY_ID[id]?.label ?? id;
}

/** Filing statuses with our labels (the schema's -IN statuses are joint/separate with the other party). */
export const FILINGS: [FilingStatus, string][] = [
  ['SINGLE', 'Single'], ['HHMLA', 'Head of household'], ['MFJIN', 'Joint, with other party'], ['MFJOUT', 'Joint, with new spouse'],
  ['MFSIN', 'Separate, spouse is other party'], ['MFSOUT', 'Separate, spouse is new spouse'],
];
export const filingLabel = (s: FilingStatus): string => FILINGS.find((f) => f[0] === s)?.[1] ?? s;

export interface FlatLine {
  row: GridRow;
  sub: SubRow | null;
  /** Visible in the grid (composite parent expanded, and not hidden as a zero row). */
  visible: boolean;
  /** 0-based index among visible lines (for alternate shading); -1 when hidden. */
  shade: number;
}

export interface FlattenOptions {
  /** Hide money rows that are zero for both parties (structural rows are always kept). */
  hideZero?: boolean;
  /** Both-party zero test for a row or detail line. */
  isZero?: (row: GridRow, sub: SubRow | null) => boolean;
}

/** Rows that are always shown, even when zero rows are hidden. */
export const REQUIRED_ROWS = new Set(['party', 'nkids', 'ncp', 'filing', 'exempt', 'wages']);

/** Grid lines in display order with visibility and shading (pure; drives the grid and the bench report). */
export function flattenRows(rows: GridRow[], expanded: Record<string, boolean>, opts: FlattenOptions = {}): FlatLine[] {
  const out: FlatLine[] = [];
  let shade = 0;
  for (const row of rows) {
    const rowZero = !!opts.hideZero && !REQUIRED_ROWS.has(row.id) && !!opts.isZero?.(row, null);
    out.push({ row, sub: null, visible: !rowZero, shade: rowZero ? -1 : shade++ });
    for (const sub of row.subs ?? []) {
      const subZero = !!opts.hideZero && sub.kind === 'money' && !!opts.isZero?.(row, sub);
      const vis = !rowZero && !!expanded[row.id] && !subZero;
      out.push({ row, sub, visible: vis, shade: vis ? shade++ : -1 });
    }
  }
  return out;
}

/** Monthly value of a money row/line for one party (composite rows: their total). */
export function moneyValue(c: Case, p: PartyIndex, row: GridRow, sub: SubRow | null): number {
  if (sub) { const v = sub.get(c, p); return typeof v === 'number' ? v : 0; }
  if (row.kind === 'money' && row.get) return row.get(c.parties[p]);
  if (row.total) return row.total(c, p);
  return 0;
}
